// React
import React, { useState } from "react";
// Services
import { getValidatorIdForType, validateResource, parseValidateResponse } from "../../services/validatorService";
// Components
import LoadingButton from "../LoadingButton/LoadingButton";
// Translation
import i18n from "i18next";
// FontAwesome
import { faCircleCheck, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

////////////////////////////////
//           Props            //
////////////////////////////////

interface ValidationButtonProps {
  entry: any;
  index: number;
  onValidated: (entry: any) => void;
  onOpenAlert: (index: number) => void;
  setIsDataLoading: (index: number | null) => void;
  createToast: (toast: any) => void;
  isDisabled?: boolean;
  isOutput?: boolean;
  contextType?: "Questionnaire" | "StructureMap";
}

///////////////////////////////
//         Component         //
///////////////////////////////

const ValidationButton: React.FC<ValidationButtonProps> = ({
  entry,
  index,
  onValidated,
  onOpenAlert,
  setIsDataLoading,
  createToast,
  isDisabled,
  isOutput,
  contextType,
}) => {
  ///////////////////////////
  //      Constants        //
  ///////////////////////////

  const [isLoading, setIsLoading] = useState(false);

  const validatorId =
    contextType === "Questionnaire"
      ? process.env.REACT_APP_VALIDATOR_FHIR_JSON_ID
      : getValidatorIdForType(entry.type ?? "JSON");

  ///////////////////////////
  //        Action         //
  ///////////////////////////

  /**
   * Handles the click event to validate the resource.
   *
   * @param e The click event
   * @returns
   */
  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsLoading(true);
    setIsDataLoading(index);
    onOpenAlert(index);
    try {
      const response = await validateResource(entry, validatorId);
      parseValidateResponse(response, entry);
      onValidated(entry);
    } catch (error: any) {
      setIsDataLoading(null);
      setIsLoading(false);
      createToast({
        title: i18n.t("text.errorvalidateheader"),
        icon: faTriangleExclamation,
        body: error?.response
          ? JSON.stringify(error.response.data.issue[0].diagnostics, null, "\t")
          : i18n.t("text.errorvalidatetext"),
      });
      return;
    }
    setIsDataLoading(null);
    setIsLoading(false);
  };

  ///////////////////////////
  //        Content        //
  ///////////////////////////

  if (!validatorId || validatorId === "undefined") return null;

  return (
    <LoadingButton
      isLoading={isLoading}
      onClick={handleClick}
      enabledTooltipText={i18n.t("tooltip.validationresource")}
      disabledTooltipText={i18n.t("tooltip.disabled")}
      icon={faCircleCheck}
      isDisabled={isDisabled}
      isOutput={isOutput}
    />
  );
};

export default ValidationButton;
